import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Botao, Cartao } from '../../componentes/base';
import { Alternador, Selecao } from '../../componentes/formulario';
import { ICONES, ICONES_PADRAO, iconesEfetivos, nomeDoIcone } from '../../nucleo/icones-navegacao';
import type { PropsSecaoWorkspace } from './Subsistemas';

/** Grupo da lista de trocas: o primeiro nível (módulos e grupos), as abas de cada módulo e as abas internas. */
export function gruposDeIcones(): { grupo: string; chaves: string[] }[] {
  const mapa = new Map<string, string[]>();
  for (const chave of Object.keys(ICONES_PADRAO)) {
    const grupo = chave.startsWith('grupo:') || chave.startsWith('modulo:') ? 'primeiro' : chave.slice(0, chave.indexOf('.'));
    mapa.set(grupo, [...(mapa.get(grupo) ?? []), chave]);
  }
  return [...mapa.entries()].map(([grupo, chaves]) => ({ grupo, chaves }));
}

/** Rótulo de uma chave de ícone no idioma (grupo do Cadastros, módulo, aba de módulo ou aba interna). */
export function rotuloDaChave(t: (k: string) => string, chave: string): string {
  if (chave.startsWith('grupo:')) {
    const [menu, g] = chave.slice(6).split('.') as [string, string];
    return t(`navegacao.${menu}.${g}`);
  }
  if (chave.startsWith('modulo:')) return t(`${chave.slice(7)}:nome`);
  const [dono, aba] = chave.split('.') as [string, string];
  if (dono === 'conta') return aba === 'assistentes' ? t('assistentes.aba') : aba === 'lixeira' ? t('lixeira.aba') : t(`conta.abas.${aba}`);
  if (dono === 'notificacoes') return t(`notificacoes.abas.${aba}`);
  if (dono === 'usuarios') return t(`admin:usuarios.subabas.${aba}`);
  return t(`${dono}:abas.${aba}`);
}

function rotuloDoGrupo(t: (k: string) => string, tc: (k: string) => string, grupo: string): string {
  if (grupo === 'primeiro') return tc('icones.primeiroNivel');
  if (grupo === 'conta') return t('conta.titulo');
  if (grupo === 'notificacoes') return t('notificacoes.titulo');
  if (grupo === 'usuarios') return t('admin:abas.usuarios');
  return t(`${grupo}:nome`);
}

/**
 * D2 (ícones nas abas): seção do workspace em Admin > Configurações > Sub-sistemas. Liga e desliga os ícones, escolhe se
 * aparecem no celular e troca o ícone de cada aba (do primeiro nível e das sub-abas). Grava com a arrumação do workspace.
 */
export function IconesNasAbas({ config, aoMudar, aoSalvar, salvando }: PropsSecaoWorkspace) {
  const { t } = useTranslation('configuracoes');
  const { t: tg } = useTranslation();
  const idAtivo = useId();
  const idCelular = useId();
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const icones = config.icones ?? {};
  // D2: ligado por padrão; só o desligar explícito tira.
  const ativo = !!iconesEfetivos(icones).ativo;
  const grupos = useMemo(() => gruposDeIcones(), []);
  // Captura do Gustavo (13/10): o ícone pelo nome no idioma do app ("Padrão (Rede)"), nunca o do lucide em inglês.
  const nomeIcone = (n: string) => t(`icones.nomes.${n}`);
  const nomes = useMemo(() => Object.keys(ICONES).sort((a, b) => t(`icones.nomes.${a}`).localeCompare(t(`icones.nomes.${b}`))), [t]);
  const mudarIcones = (n: typeof icones) => aoMudar({ ...config, icones: n });
  const trocar = (chave: string, nome: string) => {
    const trocas = { ...(icones.trocas ?? {}) };
    if (!nome || nome === ICONES_PADRAO[chave]) delete trocas[chave];
    else trocas[chave] = nome;
    mudarIcones({ ...icones, trocas });
  };

  return (
    <Cartao titulo={t('icones.titulo')}>
      <div className="flex min-w-0 flex-col gap-4">
        <p className="text-sm text-tinta-3">{t('icones.ajuda')}</p>
        <div className="flex min-w-0 items-start justify-between gap-3">
          <label htmlFor={idAtivo} className="text-sm font-medium text-tinta-1">
            {t('icones.ativo')}
          </label>
          <Alternador id={idAtivo} marcado={ativo} aoMudar={(v) => mudarIcones({ ...icones, ativo: v })} />
        </div>
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0">
            <label htmlFor={idCelular} className="text-sm font-medium text-tinta-1">
              {t('icones.celular')}
            </label>
            <p className="text-xs text-tinta-3">{t('icones.celularAjuda')}</p>
          </div>
          <Alternador id={idCelular} marcado={icones.celular !== false} desabilitado={!ativo} aoMudar={(v) => mudarIcones({ ...icones, celular: v })} />
        </div>
        <div className="flex min-w-0 flex-col gap-2">
          <h3 className="text-sm font-semibold text-tinta-1">{t('icones.trocar')}</h3>
          <ul className="flex min-w-0 flex-col divide-y divide-borda rounded-lg border border-borda">
            {grupos.map(({ grupo, chaves }) => {
              const aberto = abertos.has(grupo);
              const idLista = `icones-${grupo}`;
              return (
                <li key={grupo} className="min-w-0">
                  <button
                    type="button"
                    aria-expanded={aberto}
                    aria-controls={idLista}
                    onClick={() => setAbertos((x) => (x.has(grupo) ? new Set([...x].filter((y) => y !== grupo)) : new Set([...x, grupo])))}
                    className="flex w-full min-w-0 items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-tinta-1 hover:text-acento-forte"
                  >
                    {aberto ? <ChevronDown className="size-4 shrink-0 text-tinta-3" aria-hidden /> : <ChevronRight className="size-4 shrink-0 text-tinta-3" aria-hidden />}
                    <span className="min-w-0 truncate" title={rotuloDoGrupo(tg, t, grupo)}>{rotuloDoGrupo(tg, t, grupo)}</span>
                  </button>
                  {aberto && (
                    <ul id={idLista} className="flex min-w-0 flex-col gap-1 border-t border-borda bg-sup-2 px-3 py-2">
                      {chaves.map((chave) => {
                        const nome = nomeDoIcone(chave, icones.trocas) ?? '';
                        const Icone = ICONES[nome];
                        const rotulo = rotuloDaChave(tg, chave);
                        return (
                          <li key={chave} className="flex min-w-0 flex-wrap items-center justify-between gap-2 py-1 pl-6 text-sm text-tinta-2" data-icone-chave={chave}>
                            <span className="flex min-w-0 items-center gap-1.5">
                              {Icone && <Icone className="size-4 shrink-0" aria-hidden />}
                              <span className="min-w-0 truncate" title={rotulo}>{rotulo}</span>
                            </span>
                            <div className="w-56 max-w-full">
                              <Selecao
                                aria-label={t('icones.iconeDe', { nome: rotulo })}
                                value={icones.trocas?.[chave] ?? ''}
                                onChange={(e) => trocar(chave, e.target.value)}
                                opcoes={[{ valor: '', rotulo: t('icones.padrao', { nome: nomeIcone(ICONES_PADRAO[chave]!) }) }, ...nomes.map((n) => ({ valor: n, rotulo: nomeIcone(n) }))]}
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
        <div className="flex justify-end">
          <Botao carregando={salvando} onClick={aoSalvar}>
            {t('subsistemas.salvar')}
          </Botao>
        </div>
      </div>
    </Cartao>
  );
}
